import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { activitiesAPI } from "@/services/api";
import type { Activity, ActivityFilters } from "@/types/activity";

interface ActivitiesState {
  myActivities: Activity[];
  currentActivity: Activity | null;
  page: number;
  hasMore: boolean;
  filters: ActivityFilters;
  status: "idle" | "loading" | "success" | "error";
  error: string | null;
}

const initialState: ActivitiesState = {
  myActivities: [],
  currentActivity: null,
  page: 1,
  hasMore: true,
  filters: {
    sort: "date",
    sport: "all",
    page: 1,
    sortOrder: -1,
    isPublic: false,
  },
  status: "idle",
  error: null,
};

export const fetchActivities = createAsyncThunk(
  "activities/fetchActivities",
  async (
    params: { page: number; sort: string; sport: string; sortOrder: number; isPublic?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await activitiesAPI.getActivities(params);
      return response.data;
    } catch {
      return rejectWithValue("Failed to fetch activities");
    }
  }
);

export const fetchActivity = createAsyncThunk(
  "activities/fetchActivity",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await activitiesAPI.getActivity(id);
      // Transform backend 'name' to 'title'
      const activity = response.data;
      return {
        ...activity,
        title: activity.name || activity.title || "Untitled Activity",
      };
    } catch {
      return rejectWithValue("Failed to fetch activity");
    }
  }
);

const activitiesSlice = createSlice({
  name: "activities",
  initialState,
  reducers: {
    setSortBy: (
      state,
      action: PayloadAction<"date" | "distance" | "pace" | "ascent" | "technicity">
    ) => {
      state.filters.sort = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myActivities = [];
      state.hasMore = true;
      state.status = "idle"; // Trigger refetch
    },
    setSportFilter: (state, action: PayloadAction<string>) => {
      state.filters.sport = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myActivities = [];
      state.hasMore = true;
      state.status = "idle"; // Trigger refetch
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.filters.sortOrder = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myActivities = [];
      state.hasMore = true;
      state.status = "idle"; // Trigger refetch
    },
    setPublicFilter: (state, action: PayloadAction<boolean>) => {
      state.filters.isPublic = action.payload;
      state.filters.page = 1;
      state.page = 1;
      state.myActivities = [];
      state.hasMore = true;
      state.status = "idle"; // Trigger refetch
    },
    resetActivities: (state) => {
      state.myActivities = [];
      state.page = 1;
      state.filters.page = 1;
      state.hasMore = true;
      state.status = "idle"; // Reset status to trigger fetch
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivities.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchActivities.fulfilled, (state, action) => {
        state.status = "success";
        // Backend returns array directly, not { activities: [] }
        const activities = Array.isArray(action.payload) ? action.payload : [];

        // Transform backend data: 'name' -> 'title'
        const transformedActivities = activities.map((activity: unknown) => ({
          ...(activity as Activity),
          title:
            (activity as { name?: string }).name ||
            (activity as Activity).title ||
            "Untitled Activity",
        }));

        // If page is 1, replace all activities, otherwise append
        if (state.filters.page === 1) {
          state.myActivities = transformedActivities;
        } else {
          // Filter out duplicates by _id
          const existingIds = new Set(state.myActivities.map((a) => a._id));
          const newActivities = transformedActivities.filter((a) => !existingIds.has(a._id));
          state.myActivities = [...state.myActivities, ...newActivities];
        }

        state.hasMore = activities.length >= 20;
        state.filters.page = state.filters.page + 1;
      })
      .addCase(fetchActivities.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload as string;
      })
      .addCase(fetchActivity.pending, (state) => {
        state.status = "loading";
        state.currentActivity = null;
      })
      .addCase(fetchActivity.fulfilled, (state, action) => {
        state.status = "success";
        state.currentActivity = action.payload;
      })
      .addCase(fetchActivity.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload as string;
      });
  },
});

export const { setSortBy, setSportFilter, setSortOrder, setPublicFilter, resetActivities } =
  activitiesSlice.actions;
export default activitiesSlice.reducer;
